/**
 * ChatLauncherContext — programmatic way to open the global AI chat panel
 * with a set of pre-attached entities (e.g., "Ask Luma about these 5 tasks").
 *
 * The panel itself lives inside GlobalAIButton. This context acts as a
 * one-shot message bus: any component can call openChat({ aboutEntities })
 * and GlobalAIButton consumes the request, opens the panel with a fresh
 * session, and clears the request.
 */
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

/** A single entity reference to attach to a new chat session. */
export interface ChatLaunchEntity {
  /** Entity reference in the format "{entity_type}:{uuid}", e.g. "tasks:abc-123" */
  ref: string;
  /** Display label shown on the chip, e.g. the task title */
  label: string;
  /** Optional entity type for icon selection (e.g., 'item', 'task', 'deal') */
  type?: string;
}

export interface ChatLaunchRequest {
  aboutEntities: ChatLaunchEntity[];
  /** Optional override for the panel title */
  title?: string;
  /** Optional override for the input placeholder */
  inputPlaceholder?: string;
  /**
   * Optional text to pre-fill the chat input with for the new session. Unlike
   * `inputPlaceholder` (a greyed-out hint), this seeds the actual editable
   * input so the user can refine and send it. Used by dashboard suggestion
   * chips (LumaQuickWidget).
   */
  initialMessage?: string;
}

interface ChatLauncherValue {
  /** Pending launch request; GlobalAIButton consumes and clears it. */
  request: ChatLaunchRequest | null;
  openChat: (request: ChatLaunchRequest) => void;
  clearRequest: () => void;
}

const ChatLauncherCtx = createContext<ChatLauncherValue | undefined>(undefined);

export function ChatLauncherProvider({ children }: { children: ReactNode }) {
  const [request, setRequest] = useState<ChatLaunchRequest | null>(null);

  const openChat = useCallback((r: ChatLaunchRequest) => {
    setRequest(r);
  }, []);

  const clearRequest = useCallback(() => {
    setRequest(null);
  }, []);

  const value = useMemo(
    () => ({ request, openChat, clearRequest }),
    [request, openChat, clearRequest],
  );

  return <ChatLauncherCtx.Provider value={value}>{children}</ChatLauncherCtx.Provider>;
}

export function useChatLauncher(): ChatLauncherValue {
  const ctx = useContext(ChatLauncherCtx);
  if (!ctx) {
    throw new Error('useChatLauncher must be used within a ChatLauncherProvider');
  }
  return ctx;
}
